import { ProfileUpdateUserProfileMutation, ProfileUserQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Accordion } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ThreeColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { COUNTRY_CALLING_CODES_MAP, EmailNotificationFrequency, updateUserProfileSchema } from '@trace-8/trace8-schema';
import { FormEvent, useEffect, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { UPDATE_USER_PROFILE_MUTATION } from '../queries';

interface IForm {
  emailNotifications: EmailNotificationFrequency;
  firstName: string;
  lastName: string;
  officeNumber: {
    countryCode: string;
    number: string;
  };
  timezone: string;
  title: string;
}

const defaultFormValues: IForm = {
  emailNotifications: EmailNotificationFrequency.None,
  firstName: '',
  lastName: '',
  officeNumber: null,
  timezone: '',
  title: ''
};

const countryCodeSelectOptions = Object.entries(COUNTRY_CALLING_CODES_MAP).map(([key, value]) => ({ label: value, value: key }));

interface EditProfilePageProps {
  user: ProfileUserQuery['user'];
  getUpdatedUserData: (updatedUserData: ProfileUpdateUserProfileMutation['updateUserProfile']) => void;
  handleClosePage: () => void;
}

const EditProfilePage = ({ user, getUpdatedUserData, handleClosePage }: EditProfilePageProps) => {
  const { isMobile } = useResponsive();
  const { updateUserData } = useAuth();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, dirtyFields }
  } = useForm<IForm>({ defaultValues: defaultFormValues, resolver: zodResolver(updateUserProfileSchema) as Resolver<IForm> });

  const [allTimezonesSelectOptions] = useState<ISelectValue[]>(() =>
    Intl.supportedValuesOf('timeZone').map((timezone) => createSimpleSelectOption(timezone))
  );

  const [updateUserProfile, { loading: updateUserProfileLoading }] = useMutation(UPDATE_USER_PROFILE_MUTATION);

  useEffect(() => {
    if (!user) return;

    reset((formValues) => ({
      ...formValues,
      firstName: user.firstName,
      lastName: user.lastName,
      title: user?.title ?? '',
      timezone: user.timezone,
      officeNumber: user.officePhoneNumber as { countryCode: string; number: string },
      emailNotifications: user?.emailNotifications
        ? EmailNotificationFrequency[user.emailNotifications as keyof typeof EmailNotificationFrequency]
        : EmailNotificationFrequency.None
    }));
  }, [user]);

  const prepareFormDataForSubmission = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { officeNumber } = getValues();

    if (!officeNumber?.countryCode && !officeNumber?.number) {
      setValue('officeNumber', null);
    }

    handleSubmit(onSubmit, (error) => {
      console.log(error);
    })();
  };

  const onSubmit = async (data: IForm) => {
    try {
      setErrorMessage(null);
      const { emailNotifications, firstName, lastName, officeNumber, timezone, title } = data;

      const { data: updateUserProfileData } = await updateUserProfile({
        variables: {
          data: {
            ...(dirtyFields?.emailNotifications && {
              emailNotifications: getEnumKeyByValue(EmailNotificationFrequency, emailNotifications) as EmailNotificationFrequency
            }),
            ...(dirtyFields?.firstName && { firstName }),
            ...(dirtyFields?.lastName && { lastName }),
            ...(dirtyFields?.timezone && { timezone }),
            ...(dirtyFields?.officeNumber && { officeNumber: officeNumber }),
            ...(dirtyFields?.title && { title })
          }
        }
      });

      const updatedOfficeNumberCountryCode = updateUserProfileData?.updateUserProfile?.officePhoneNumber?.countryCode;
      const updatedOfficeNumber = updateUserProfileData?.updateUserProfile?.officePhoneNumber?.number;

      updateUserData({
        ...updateUserProfileData.updateUserProfile,
        ...(updatedOfficeNumberCountryCode && { officeNumberCountryCode: updatedOfficeNumberCountryCode }),
        ...(updatedOfficeNumber && { officeNumber: updatedOfficeNumber })
      });
      getUpdatedUserData(updateUserProfileData.updateUserProfile);
      handleClosePage();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {updateUserProfileLoading && (
        <ModalWrappedContent
          isOpen={updateUserProfileLoading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClosePage} backToTitle="Profile" />} />
        <PageTitle title="Edit Profile" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <ModifyUserForm id="modifyUserForm" onSubmit={prepareFormDataForSubmission}>
            <Accordion title="User Details">
              <SectionBody>
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="First Name"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        isRequiredField
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="Last Name"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        isRequiredField
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="title"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="Title"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        size="md"
                      />
                    )}
                  />
                </ThreeColumnWrapper>
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Input label="Email" value={user?.email || ''} styleType="disabled" size="md" />
                  <PhoneFields>
                    <Select
                      label="Country Code"
                      value={user?.mobilePhoneNumber?.countryCode ?? ''}
                      options={countryCodeSelectOptions}
                      size="md"
                      width="200px"
                      styleType="disabled"
                    />
                    <Input
                      label="Mobile"
                      value={user?.mobilePhoneNumber?.number ?? ''}
                      size="md"
                      styleType="disabled"
                      tooltipWidth={300}
                      tooltip="When Two Factor Authentication is enabled the user will be prompted to add a mobile number when logging in for the first time."
                    />
                  </PhoneFields>
                  <PhoneFields>
                    <Controller
                      name="officeNumber.countryCode"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          label="Country Code"
                          value={value || ''}
                          setValue={onChange}
                          options={countryCodeSelectOptions}
                          errorText={error?.message}
                          size="md"
                          width="200px"
                        />
                      )}
                    />
                    <Controller
                      name="officeNumber.number"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Other Phone"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          size="md"
                        />
                      )}
                    />
                  </PhoneFields>
                </ThreeColumnWrapper>
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="emailNotifications"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Email Notifications"
                        value={value || ''}
                        options={Object.entries(EmailNotificationFrequency).map(([key, value]) => ({ label: value, value: key }))}
                        setValue={onChange}
                        errorText={error?.message}
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="timezone"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Timezone"
                        value={value || ''}
                        options={allTimezonesSelectOptions}
                        setValue={onChange}
                        errorText={error?.message}
                        withSearch
                        isRequiredField
                        size="md"
                      />
                    )}
                  />
                </ThreeColumnWrapper>
              </SectionBody>
            </Accordion>
            <FormFooter onCancel={handleClosePage} disableSaveButton={!!Object.keys(errors || {}).length || updateUserProfileLoading} />
          </ModifyUserForm>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export default EditProfilePage;

const ModifyUserForm = styled.form`
  margin: 50px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const PhoneFields = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;
